import React, { useState } from 'react';
import { Modal } from '@consta/uikit/Modal';
import { Button } from '@consta/uikit/Button';
import { TextField } from '@consta/uikit/TextField';
import { Combobox } from '@consta/uikit/Combobox';
import { Text } from '@consta/uikit/Text';
import { DragNDropField } from '@consta/uikit/DragNDropField';
import { Attachment } from '@consta/uikit/Attachment';
import { IconUpload } from '@consta/icons/IconUpload';
import { useUploadExcelMutation, useUpdateChartMutation } from '../../../services/chartService';
import classes from './AddGraphic.module.scss';

interface AddChartModalProps {
  isOpen: boolean;
  setIsOpen?: (isOpen: boolean) => void
  handleCloseModal: () => void;
  onChartCreated: () => void;
  wellUuid: string; // Получаем UUID из родительского компонента
}

interface UploadResponse {
  uuid: string;
  parameters: string[];
}

interface ParameterItem {
  id: string;
  label: string;
}

const AddGraphic: React.FC<AddChartModalProps> = ({ 
  isOpen, 
  setIsOpen,
  handleCloseModal, 
  onChartCreated,
  wellUuid 
}) => {
  const [uploadExcel] = useUploadExcelMutation();
  const [updateChart] = useUpdateChartMutation();
  
  const [files, setFiles] = useState<File[]>([]);
  const [chartName, setChartName] = useState('');
  const [comment, setComment] = useState('');
  const [selectedParameter, setSelectedParameter] = useState<ParameterItem | null>(null);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadData, setUploadData] = useState<UploadResponse | null>(null);

  // Берем первый файл из массива
  const file = files[0];

  // DragNDrop отключается после загрузки файла (до отправки на сервер)
  const isDragNDropDisabled = isLoading || !!uploadData || !!file;

  // Преобразуем параметры из ответа в формат для Combobox
  const parameterItems: ParameterItem[] = uploadData?.parameters?.map((param, index) => ({
    id: index.toString(),
    label: param
  })) || [];

  const handleDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0 && !isDragNDropDisabled) {
      // Проверяем расширение файла
      const validFiles = acceptedFiles.filter(f => f.name.match(/\.(xlsx|xls)$/));
      
      if (validFiles.length === 0) {
        setError('Пожалуйста, загрузите файл Excel (.xlsx или .xls)');
        return;
      }

      setFiles(validFiles.slice(0, 1)); // Берем только первый файл
      setError('');
    }
  };

  const handleRemoveFile = () => {
    setFiles([]);
  };

  const handleUploadExcel = async () => {
    if (!file) {
      setError('Пожалуйста, выберите файл');
      return;
    }

    if (!chartName) {
      setError('Введите название графика');
      return;
    }

    if (!wellUuid) {
      setError('Ошибка: не выбрана скважина');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      // Создаем FormData для multipart запроса
      const formData = new FormData();
      
      // Добавляем файл
      formData.append('file', file);
      
      // Добавляем остальные данные как поля формы
      formData.append('name', chartName);
      formData.append('well', wellUuid); // Используем переданный wellUuid
      formData.append('comment', comment || '');

      //('Sending multipart form data:');
      //('- file:', file.name, file.type, file.size);
      //('- name:', chartName);
      //('- well_uuid:', wellUuid);

      const result = await uploadExcel(formData).unwrap();
      
      if (result && result.uuid) {
        setUploadData(result);
      }
    } catch (err) {
      setError('Ошибка при загрузке файла. Пожалуйста, попробуйте еще раз.');
      console.error('Upload error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateChart = async () => {
    if (!selectedParameter) {
      setError('Выберите параметр для построения графика');
      return;
    }

    if (!uploadData?.uuid) {
      setError('Ошибка: не найден UUID графика');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      // Создаем график
      const result = await updateChart({
        uuid: uploadData.uuid,
        comment: comment || '',
        build_by_parameter: selectedParameter.label
      }).unwrap();

      //('График успешно создан:', result);
      
      // После успешного создания вызываем колбэк
      onChartCreated();
      handleClose();
      
    } catch (err) {
      setError('Ошибка при создании графика');
      console.error('Create chart error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setUploadData(null);
    setChartName('');
    setComment('');
    setSelectedParameter(null);
    setError('');
    setIsLoading(false);
    handleCloseModal();
  };

  const canUpload = file && chartName && wellUuid && !isLoading;
  const canCreate = uploadData && selectedParameter && !isLoading;

  return (
    <Modal 
      isOpen={isOpen} 
      // handleCloseModal={handleClose}
      className={classes.modal}
      hasOverlay
    >
      <div className={classes.modalContent}>
        <Text size="xl" weight="bold" className={classes.title}>
          Добавить график
        </Text>

        {/* Информация о выбранной скважине */}
        {wellUuid && (
          <Text size="s" view="secondary" className={classes.wellInfo}>
            Скважина: {wellUuid}
          </Text>
        )}

        {/* DragNDropField из Consta - отключается после загрузки файла */}
        <DragNDropField 
          onDropFiles={handleDrop}
          accept={['.xlsx', '.xls']}
          multiple={false}
          disabled={isDragNDropDisabled}
        >
          {({ openFileDialog }) => (
            <div className={classes.dropzoneContent}>
              {/*@ts-ignore */}
              <IconUpload size="m" className={classes.uploadIcon} />
              <Text size="m" align="center">
                {file ? 'Файл загружен' : 'Перетащите файл Excel сюда или нажмите для выбора'}
              </Text>
              <Text size="s" view="secondary" align="center">
                Поддерживаются файлы .xlsx и .xls
              </Text>
              {!file && (
                <Button
                  size="s"
                  view="ghost"
                  label="Выбрать файл"
                  onClick={openFileDialog}
                  disabled={isDragNDropDisabled}
                />
              )}
            </div>
          )}
        </DragNDropField>

        {/* Отображение загруженного файла */}
        {file && (
          <div className={classes.fileInfo}>
            {/*@ts-ignore */}
            <Attachment
              fileName={file.name}
              fileExtension={file.name.split('.').pop() || ''}
              fileDescription={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
              onClick={handleRemoveFile}
              // buttonIcon="close"
              //@ts-ignore
              disabled={isLoading || !!uploadData}
            />
          </div>
        )}

        {/* Поле названия - всегда видно */}
        <TextField
          label="Название графика"
          value={chartName}
          onChange={( value ) => setChartName(value || '')}
          placeholder="Введите название графика"
          required
          disabled={isLoading || !!uploadData}
        />

        {/* Селектор параметра и комментарий - появляются после успешной загрузки */}
        {uploadData && (
          <div className={classes.formSection}>
            <Combobox
              label="Параметр для построения"
              items={parameterItems}
              value={selectedParameter}
              onChange={setSelectedParameter}
              placeholder="Выберите параметр"
              required
              disabled={isLoading}
            />
            
            <TextField
              label="Комментарий"
              value={comment}
              onChange={( value ) => setComment(value || '')}
              placeholder="Введите комментарий (необязательно)"
              type="textarea"
              rows={3}
              disabled={isLoading}
            />
          </div>
        )}

        {/* Сообщение об ошибке */}
        {error && (
          <Text view="alert" size="s" className={classes.error}>
            {error}
          </Text>
        )}

        {/* Кнопки */}
        <div className={classes.buttons}>
          <Button 
            label="Отмена" 
            view="secondary" 
            onClick={handleClose}
            disabled={isLoading}
          />
          
          {!uploadData ? (
            <Button 
              label={isLoading ? "Загрузка..." : "Выбрать параметр"} 
              onClick={handleUploadExcel}
              disabled={!canUpload}
              loading={isLoading}
            />
          ) : (
            <Button 
              label={isLoading ? "Создание..." : "Создать график"} 
              onClick={handleCreateChart}
              disabled={!canCreate}
              loading={isLoading}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddGraphic;